import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, X, Check, Download, KeyRound, Power, Pencil, ShieldCheck, Briefcase, UserCog, ArrowRight, Info } from 'lucide-react';
import API from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from './ChangePasswordModal';

const ROLE_META = {
  ca:          { label: 'CA',          color: 'bg-blue-100 text-blue-700',    icon: Briefcase },
  admin:       { label: 'Admin',       color: 'bg-purple-100 text-purple-700', icon: UserCog },
  super_admin: { label: 'Super Admin', color: 'bg-[#6C63FF]/15 text-[#6C63FF]', icon: ShieldCheck },
};

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'admin',
  phone: '',
};

function Avatar({ name, size = 40 }) {
  const initials = name
    ? name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  return (
    <div
      className="rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-heading font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
}

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block text-[11px] font-body font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function MemberCard({ member, currentUserId, onToggle, onEdit, onChangePassword }) {
  const [toggling, setToggling] = useState(false);
  const isSelf = member.id === currentUserId;

  const handleToggle = async () => {
    if (isSelf) return;
    setToggling(true);
    await onToggle(member.id, !member.is_active);
    setToggling(false);
  };

  return (
    <div className={`bg-white border border-[#E8E6F8] rounded-xl p-4 flex items-center gap-4 transition-all hover:border-[#6C63FF]/30 hover:shadow-sm ${!member.is_active ? 'opacity-50' : ''}`}>
      <Avatar name={member.name} size={44} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-body font-medium text-[#1A1A2E] text-sm">{member.name}</p>
          <RoleBadge role={member.role} />
          {isSelf && (
            <span className="text-[10px] bg-gray-100 text-gray-500 font-body px-1.5 py-0.5 rounded-full">You</span>
          )}
          {!member.is_active && (
            <span className="text-[10px] bg-red-100 text-red-500 font-body px-1.5 py-0.5 rounded-full">Deactivated</span>
          )}
        </div>
        <p className="text-xs text-[#6B7280] font-body mt-0.5">{member.email}</p>
        {member.phone && <p className="text-xs text-[#9CA3AF] font-body">{member.phone}</p>}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onEdit(member)}
          className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280] hover:text-[#6C63FF] transition-colors"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onChangePassword(member)}
          className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280] hover:text-[#6C63FF] transition-colors"
          title="Change Password"
        >
          <KeyRound className="h-3.5 w-3.5" />
        </button>
        {!isSelf && (
          <button
            onClick={handleToggle}
            disabled={toggling}
            title={member.is_active ? 'Deactivate' : 'Activate'}
            className={`p-1.5 rounded-lg transition-colors ${
              member.is_active
                ? 'hover:bg-red-50 text-[#6B7280] hover:text-red-500'
                : 'hover:bg-green-50 text-[#6B7280] hover:text-green-600'
            }`}
          >
            <Power className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function CreateForm({ onSave, onCancel }) {
  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await API.post('/admin/team', form);
      onSave(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account.');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#E8E6F8] rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[#1A1A2E] text-base">Add New Login Account</h3>
        <button onClick={onCancel} className="text-[#9CA3AF] hover:text-[#6B7280]"><X className="h-4 w-4" /></button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm font-body p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              placeholder="e.g. Priya Sharma"
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Email Address *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              required
              placeholder="e.g. priya@myca.in"
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Password * (min 8 chars)</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              required
              minLength={8}
              placeholder="Create a strong password"
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Role *</label>
            <select
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            <p className="text-[10px] text-[#9CA3AF] font-body mt-1">
              CAs are created from the CA Profiles page (where you also fill in profile details).
            </p>
          </div>

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Phone (optional)</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>
        </div>

        {/* Role info */}
        <div className="bg-[#F5F4FB] rounded-lg p-3 text-xs font-body text-[#4A4A60] space-y-1">
          {form.role === 'admin' && <p><strong>Admin:</strong> Can see all orders, all users, CA profiles, and contacts.</p>}
          {form.role === 'super_admin' && <p><strong>Super Admin:</strong> Full access to all features including this Team Management page.</p>}
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 h-10 px-5 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 text-white rounded-full text-sm font-body font-medium transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            {saving ? 'Creating…' : 'Create Account'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-10 px-5 border border-[#E8E6F8] rounded-full text-sm font-body text-[#6B7280] hover:bg-[#F5F4FB] transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function EditForm({ member, onSave, onCancel }) {
  const [form, setForm]     = useState({ name: member.name, phone: member.phone || '', role: member.role });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await API.put(`/admin/team/${member.id}`, form);
      onSave(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update account.');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#E8E6F8] rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[#1A1A2E] text-base">Edit Account — {member.email}</h3>
        <button onClick={onCancel} className="text-[#9CA3AF] hover:text-[#6B7280]"><X className="h-4 w-4" /></button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm font-body p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>
          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>
          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">Role</label>
            <select
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              disabled={member.role === 'ca'}
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF] disabled:bg-[#F5F4FB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed"
            >
              {member.role === 'ca' && <option value="ca">CA (Chartered Accountant)</option>}
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            {member.role === 'ca' && (
              <p className="text-[10px] text-[#9CA3AF] font-body mt-1">
                Manage CA accounts from the CA Profiles page.
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 h-10 px-5 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 text-white rounded-full text-sm font-body font-medium transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-10 px-5 border border-[#E8E6F8] rounded-full text-sm font-body text-[#6B7280] hover:bg-[#F5F4FB] transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminTeamPage() {
  const { user } = useAuth();
  const [members, setMembers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [editingMember, setEditing]   = useState(null);
  const [pwdTarget, setPwdTarget]     = useState(null);
  const [error, setError]             = useState('');

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/team');
      setMembers(data);
    } catch {
      setError('Failed to load team members.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleCreated = (newMember) => {
    setMembers((prev) => [newMember, ...prev]);
    setShowCreate(false);
  };

  const handleUpdated = (updated) => {
    setMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setEditing(null);
  };

  const handleToggle = async (memberId, newStatus) => {
    try {
      const { data } = await API.put(`/admin/team/${memberId}/status`, { is_active: newStatus });
      setMembers((prev) => prev.map((m) => (m.id === data.id ? data : m)));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update status.');
    }
  };

  // Group by role for display
  const grouped = {
    super_admin: members.filter((m) => m.role === 'super_admin'),
    admin:       members.filter((m) => m.role === 'admin'),
    ca:          members.filter((m) => m.role === 'ca'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleExport = () => {
    // Members are already in state — build CSV directly. We export all roles
    // (Super Admin / Admin / CA) in one file so leadership has a single roster.
    const headers = [
      'User ID', 'Name', 'Email', 'Phone', 'Role', 'Status', 'Created',
    ];

    // RFC 4180: quote on comma / quote / newline.
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const roleLabel = (r) => ROLE_META[r]?.label || r;
    const rows = members.map((m) => [
      `#${m.id}`,
      m.name,
      m.email,
      m.phone,
      roleLabel(m.role),
      m.is_active ? 'Active' : 'Inactive',
      m.created_at ? new Date(m.created_at).toLocaleString('en-IN') : '',
    ].map(esc).join(','));

    const csv = [headers.map(esc).join(','), ...rows].join('\r\n');
    // UTF-8 BOM so Excel renders Indian names correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `team-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const teamStats = [
    { icon: ShieldCheck, label: 'Super Admins', value: grouped.super_admin.length },
    { icon: UserCog,     label: 'Admins',       value: grouped.admin.length },
    { icon: Briefcase,   label: 'CAs',          value: grouped.ca.length },
  ];

  return (
    <div className="space-y-5">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#064E3B] via-[#047857] to-[#10B981] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#34D399]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/25 rounded-full blur-3xl" />
        <div className="relative flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Team Management
              </h1>
              <p className="text-sm text-white/85 font-body mt-0.5">
                {grouped.super_admin.length + grouped.admin.length} admin account
                {(grouped.super_admin.length + grouped.admin.length) !== 1 ? 's' : ''}
                {grouped.ca.length > 0 && <> · {grouped.ca.length} CA{grouped.ca.length !== 1 ? 's' : ''} (managed in CA Profiles)</>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Quick stats */}
            {teamStats.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-2.5 px-3.5 py-2 rounded-2xl bg-white/[0.1] backdrop-blur-sm border border-white/15"
              >
                <s.icon className="h-4 w-4 text-white/80 flex-shrink-0" />
                <div className="leading-tight">
                  <p className="text-lg font-heading font-bold text-white">{s.value}</p>
                  <p className="text-[10px] font-body uppercase tracking-wide text-white/60 -mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
            {!showCreate && !editingMember && (
              <>
                <button
                  onClick={handleExport}
                  disabled={members.length === 0}
                  title="Download team roster as CSV"
                  className="inline-flex items-center gap-1.5 h-11 px-3.5 bg-white/15 backdrop-blur-sm border border-white/20 text-white hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-sm font-body font-semibold transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-2 h-11 px-4 bg-white text-[#047857] hover:bg-white/95 rounded-xl text-sm font-body font-semibold shadow-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Account
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm font-body p-3 rounded-lg mb-5">
          {error}
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateForm onSave={handleCreated} onCancel={() => setShowCreate(false)} />
      )}

      {/* Edit form */}
      {editingMember && (
        <EditForm
          member={editingMember}
          onSave={handleUpdated}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Info banner — explain scope */}
      <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl p-3 mb-5 flex items-start gap-2.5">
        <Info className="h-4 w-4 text-[#6C63FF] mt-0.5 flex-shrink-0" />
        <p className="text-xs font-body text-[#4A4A60]">
          This page manages <strong className="text-[#1A1A2E]">Admin</strong> and <strong className="text-[#1A1A2E]">Super Admin</strong> login accounts.
          To add or manage Chartered Accountants, go to{' '}
          <Link to="/admin/ca-profiles" className="text-[#6C63FF] font-semibold hover:underline">CA Profiles</Link>.
        </p>
      </div>

      {/* Grouped member list */}
      {(grouped.super_admin.length === 0 && grouped.admin.length === 0) ? (
        <div className="text-center py-16 text-[#9CA3AF] font-body text-sm">
          No admin accounts yet. Click "Add Account" to create the first one.
        </div>
      ) : (
        <div className="space-y-6">
          {[
            { key: 'super_admin', title: 'Super Admins' },
            { key: 'admin',       title: 'Admins' },
          ].map(({ key, title }) => grouped[key].length > 0 && (
            <div key={key}>
              <p className="text-xs font-body font-semibold text-[#9CA3AF] uppercase tracking-widest mb-2">
                {title} ({grouped[key].length})
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {grouped[key].map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    currentUserId={user?.id}
                    onToggle={handleToggle}
                    onEdit={(m) => { setEditing(m); setShowCreate(false); }}
                    onChangePassword={setPwdTarget}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CA summary card — read-only pointer to CA Profiles */}
      {grouped.ca.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-body font-semibold text-[#9CA3AF] uppercase tracking-widest mb-2">
            Chartered Accountants ({grouped.ca.length})
          </p>
          <Link
            to="/admin/ca-profiles"
            className="block bg-white border border-[#E8E6F8] hover:border-[#6C63FF]/40 hover:shadow-sm rounded-xl p-4 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#6C63FF]/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-[#6C63FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-[#1A1A2E] text-sm">
                  {grouped.ca.filter((c) => c.is_active).length} active · {grouped.ca.filter((c) => !c.is_active).length} inactive
                </p>
                <p className="text-xs text-[#6B7280] font-body mt-0.5">
                  CAs are managed from the CA Profiles page (login + profile details together).
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#9CA3AF] group-hover:text-[#6C63FF] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </div>
          </Link>
        </div>
      )}

      <ChangePasswordModal
        open={!!pwdTarget}
        target={pwdTarget}
        endpoint="team"
        onClose={() => setPwdTarget(null)}
      />
    </div>
  );
}
